import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserPlus, Trash2, Plus, Users, ShieldCheck, Tag, Calendar, ExternalLink } from "lucide-react";
import { DiscordChannel, DiscordRole, GuildConfig, api } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChannelPicker } from "@/components/panel/ChannelPicker";
import { MultiSelect } from "@/components/MultiSelect";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

interface Props {
  guildId: string;
  config: GuildConfig;
  channels: DiscordChannel[];
  roles: DiscordRole[];
  onChange: (v: GuildConfig["partnership"]) => void;
}

export function PartnershipTab({ guildId, config, channels, roles, onChange }: Props) {
  const qc = useQueryClient();
  const cfg = config.partnership || {
    enabled: false,
    partnership_channel_id: "",
    partnership_category_id: "",
    manager_role_ids: [],
    partner_role_id: "",
    minimum_members: 50,
    reward_coins: 100,
    partners: []
  };

  const update = <K extends keyof typeof cfg>(k: K, v: (typeof cfg)[K]) => {
    onChange({ ...cfg, [k]: v });
  };

  // State for adding a partner manually
  const [partnerGid, setPartnerGid] = useState("");
  const [partnerName, setPartnerName] = useState("");
  const [partnerInvite, setPartnerInvite] = useState("");
  const [partnerBanner, setPartnerBanner] = useState("");
  const [partnerRep, setPartnerRep] = useState("");
  const [partnerDesc, setPartnerDesc] = useState("");

  // API mutations using React Query
  const addPartnerMut = useMutation({
    mutationFn: (partner: any) => api.addPartner(guildId, partner),
    onSuccess: (data) => {
      // Refresh local partners list in parent state
      const updatedPartners = [...(cfg.partners || []), data.partner];
      update("partners", updatedPartners);
      
      // Reset form
      setPartnerGid("");
      setPartnerName("");
      setPartnerInvite("");
      setPartnerBanner("");
      setPartnerRep("");
      setPartnerDesc("");
      
      toast.success("Partner server added successfully!");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to add partner server.");
    }
  });

  const deletePartnerMut = useMutation({
    mutationFn: (partnerId: string) => api.deletePartner(guildId, partnerId),
    onSuccess: (_, partnerId) => {
      const updatedPartners = (cfg.partners || []).filter((p: any) => p.guild_id !== partnerId);
      update("partners", updatedPartners);
      toast.success("Partnership terminated successfully.");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to delete partner.");
    }
  });

  function handleAddPartner() {
    if (!partnerGid.trim()) return toast.error("Enter Guild ID");
    if (!partnerName.trim()) return toast.error("Enter Server Name");
    if (!partnerInvite.trim()) return toast.error("Enter Invite Link");

    addPartnerMut.mutate({
      guild_id: partnerGid.trim(),
      name: partnerName.trim(),
      invite_url: partnerInvite.trim(),
      banner_url: partnerBanner.trim(),
      representative_id: partnerRep.trim(),
      description: partnerDesc.trim()
    });
  }

  return (
    <div className="space-y-6">
      {/* Overview/Hint box */}
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">How Partnerships Work</p>
        <p>
          Configure requirements and automate partnerships. Members can run `/partner add` to request a partnership. 
          The bot verifies if the target server meets your requirements (such as member counts), assigns the **Partner Representative Role**, 
          posts a beautiful embed with their banner in the **Partnership Channel**, and optionally rewards the staff member with coins!
        </p>
      </div>

      {/* Main Settings Section */}
      <Section
        icon={UserPlus}
        title="Partnership Settings"
        description="Toggle the partnership module and configure roles, channels, and automated requirements."
        enabled={cfg.enabled}
        onToggle={(v) => update("enabled", v)}
      >
        <div className="grid md:grid-cols-3 gap-4">
          <Field
            label="Announcement Channel"
            hint="Where the bot posts new partner ads automatically."
          >
            <ChannelPicker
              channels={channels}
              value={cfg.partnership_channel_id}
              onChange={(v) => update("partnership_channel_id", v === "__none__" ? "" : v)}
            />
          </Field>

          <Field
            label="Partnership Category"
            hint="Discord category for dedicated partner channels."
          >
            <Select
              value={cfg.partnership_category_id || "__none__"}
              onValueChange={(v) => update("partnership_category_id", v === "__none__" ? "" : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Pick category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None (Automatically create 'PARTNERS')</SelectItem>
                {channels
                  .filter((c) => c.type === 4)
                  .map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      📁 {c.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </Field>

          <Field
            label="Partner Representative Role"
            hint="Auto-assigned to representative users on creation."
          >
            <Select
              value={cfg.partner_role_id || "__none__"}
              onValueChange={(v) => update("partner_role_id", v === "__none__" ? "" : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Pick role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {roles.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    @{r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <Field
          label="Partnership Manager Roles"
          hint="Roles allowed to manage partnerships and run `/partner add`."
        >
          <MultiSelect
            options={roles}
            value={cfg.manager_role_ids || []}
            onChange={(v) => update("manager_role_ids", v)}
            prefix="@"
            placeholder="+ add role"
          />
        </Field>

        <div className="grid md:grid-cols-2 gap-4">
          <Field
            label="Minimum Members Requirement"
            hint="Automated member limit for partner servers. Bot blocks invites with fewer members."
          >
            <Input
              type="number"
              value={cfg.minimum_members}
              onChange={(e) => update("minimum_members", Number(e.target.value))}
              min={0}
            />
          </Field>

          <Field
            label="Staff Reward (Coins)"
            hint="Economy coins rewarded to the staff member who secures the partnership."
          >
            <Input
              type="number"
              value={cfg.reward_coins}
              onChange={(e) => update("reward_coins", Number(e.target.value))}
              min={0}
            />
          </Field>
        </div>
      </Section>

      {cfg.enabled && (
        <>
          {/* Manual Partner Registration */}
          <Card className="p-6 bg-card/50 shadow-xl border-primary/5 space-y-6">
            <div>
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Plus className="h-5 w-5 text-primary" />
                Register Partner Manually
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Register a new partner directly from the dashboard without running commands.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold">Partner Guild ID <span className="text-red-500">*</span></label>
                <Input placeholder="e.g. 123456789012345678" value={partnerGid} onChange={(e) => setPartnerGid(e.target.value)} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold">Server Name <span className="text-red-500">*</span></label>
                <Input placeholder="e.g. Pixel Community" value={partnerName} onChange={(e) => setPartnerName(e.target.value)} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold">Invite Link <span className="text-red-500">*</span></label>
                <Input placeholder="e.g. https://discord.gg/invite" value={partnerInvite} onChange={(e) => setPartnerInvite(e.target.value)} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold">Banner Image URL (Optional)</label>
                <Input placeholder="e.g. https://i.imgur.com/image.png" value={partnerBanner} onChange={(e) => setPartnerBanner(e.target.value)} />
              </div>
              <div className="space-y-1 md:col-span-2">
                <label className="text-xs font-semibold">Partner Representative Discord ID (Optional)</label>
                <Input placeholder="e.g. 987654321098765432" value={partnerRep} onChange={(e) => setPartnerRep(e.target.value)} />
              </div>
              <div className="space-y-1 md:col-span-2">
                <label className="text-xs font-semibold">Partnership Description / Ad Text</label>
                <Textarea 
                  placeholder="Welcome to our server! We offer..." 
                  value={partnerDesc} 
                  onChange={(e) => setPartnerDesc(e.target.value)} 
                  rows={3} 
                />
              </div>
            </div>

            <Button onClick={handleAddPartner} disabled={addPartnerMut.isPending} className="w-full md:w-auto gap-2">
              <UserPlus className="h-4 w-4" />
              Register Partnership
            </Button>
          </Card>

          {/* Active Partners List */}
          <Card className="overflow-hidden border-primary/10">
            <div className="p-6 border-b bg-muted/30 flex items-center justify-between gap-4">
              <div>
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-emerald-400" />
                  Active Partnerships
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Manage all currently registered and active partner servers.
                </p>
              </div>
              <Badge variant="outline" className="bg-primary/5 border-primary/20 text-xs">
                {cfg.partners?.length || 0} Partners
              </Badge>
            </div>

            <div className="p-6">
              {(!cfg.partners || cfg.partners.length === 0) ? (
                <div className="py-12 text-center space-y-2 border border-dashed border-border rounded-xl">
                  <Users className="h-10 w-10 text-muted-foreground/40 mx-auto" />
                  <p className="text-muted-foreground text-sm font-semibold">No active partnerships yet.</p>
                  <p className="text-xs text-muted-foreground/60 max-w-sm mx-auto">
                    Use the form above or the `/partner add` command in Discord to register your first partner!
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {cfg.partners.map((p: any) => (
                    <Card key={p.guild_id} className="relative overflow-hidden bg-card/30 hover:border-primary/20 transition-all p-5 flex flex-col justify-between gap-4">
                      {p.banner_url && (
                        <div className="absolute top-0 right-0 left-0 h-1.5 bg-gradient-to-r from-primary to-emerald-400" />
                      )}
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h4 className="font-extrabold text-foreground text-base truncate max-w-[200px]">{p.name}</h4>
                            <p className="text-[10px] font-mono text-muted-foreground/60">ID: {p.guild_id}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                            onClick={() => deletePartnerMut.mutate(p.guild_id)}
                            disabled={deletePartnerMut.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        {p.description && (
                          <p className="text-xs text-muted-foreground line-clamp-3 italic mt-1 leading-relaxed">
                            "{p.description}"
                          </p>
                        )}

                        <div className="pt-2 flex flex-wrap gap-2 text-[10px]">
                          <Badge variant="outline" className="flex items-center gap-1 text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            {p.partnered_at ? new Date(p.partnered_at * 1000).toLocaleDateString() : "Just now"}
                          </Badge>
                          {p.representative_id && (
                            <Badge variant="secondary" className="font-mono text-muted-foreground">
                              Rep: {p.representative_id}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {p.banner_url && (
                        <div className="h-20 w-full rounded-lg overflow-hidden border border-border bg-black/40">
                          <img src={p.banner_url} alt="Partner Banner" className="h-full w-full object-cover opacity-80 hover:opacity-100 transition-opacity" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        </div>
                      )}

                      <div className="pt-2 border-t border-border/20 flex items-center justify-between">
                        <a 
                          href={p.invite_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary-foreground font-semibold"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          Join Server
                        </a>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
